"use client";

import React from "react";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import {
  Wrench,
  Zap,
  Flame,
  Lock,
  Hammer,
  Droplets,
  Sun,
  Eye,
  AlertCircle,
} from "lucide-react";

interface ExpertiseGridProps {
  /** Array of trade/service categories */
  trades: string[];
  /** Show as compact (just badges) or expanded (grid with icons) */
  compact?: boolean;
  /** Make expanded cards clickable links to directory pages */
  linkToDirectory?: boolean;
  /** Metro slug to use when linking directory pages */
  linkMetroSlug?: string;
}

const TRADE_ICONS: Record<string, React.ReactNode> = {
  plumber: <Droplets size={18} className="text-blue-600" />,
  electrician: <Zap size={18} className="text-yellow-500" />,
  "gas-installer": <Flame size={18} className="text-red-500" />,
  locksmith: <Lock size={18} className="text-slate-700" />,
  "gate-garage-fence": <AlertCircle size={18} className="text-orange-600" />,
  "roofing-waterproofing": <Hammer size={18} className="text-amber-700" />,
  glazier: <Eye size={18} className="text-cyan-600" />,
  "solar-inverter": <Sun size={18} className="text-amber-500" />,
  "cctv-alarm": <Eye size={18} className="text-purple-600" />,
  handyman: <Wrench size={18} className="text-gray-700" />,
};

const TRADE_LABELS: Record<string, string> = {
  plumber: "Plumbing",
  electrician: "Electrical",
  "gas-installer": "Gas",
  locksmith: "Locks & Security",
  "gate-garage-fence": "Gates & Automation",
  "roofing-waterproofing": "Roofing",
  glazier: "Glass & Glazing",
  "solar-inverter": "Solar & Tech",
  "cctv-alarm": "CCTV & Alarms",
  handyman: "General Maintenance",
};

const TRADE_DIRECTORY_SLUGS: Record<string, string> = {
  plumber: "plumbers",
  electrician: "electricians",
  "gas-installer": "solar-inverter-cctv-gas",
  locksmith: "security-gates-fencing",
  "gate-garage-fence": "security-gates-fencing",
  "roofing-waterproofing": "roofing-waterproofing",
  glazier: "emergency-essentials",
  "solar-inverter": "solar-inverter-cctv-gas",
  "cctv-alarm": "solar-inverter-cctv-gas",
  handyman: "general-handyman",
};

export function ExpertiseGrid({
  trades,
  compact = false,
  linkToDirectory = false,
  linkMetroSlug = "south-africa",
}: ExpertiseGridProps) {
  if (!trades || trades.length === 0) {
    return null;
  }

  const displayTrades = trades.slice(0, compact ? 4 : 10);

  if (compact) {
    return (
      <div className="flex flex-wrap gap-2">
        {displayTrades.map((trade) => (
          <Badge key={trade} variant="outline" className="border-blue-200 bg-blue-50 text-blue-900 text-xs px-2 py-1">
            {TRADE_LABELS[trade] || trade}
          </Badge>
        ))}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-[#d9e4f7] bg-white p-4">
      <p className="text-xs font-semibold text-[#214186] uppercase tracking-wide mb-3">Areas of Expertise</p>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
        {displayTrades.map((trade) => {
          const serviceSlug = TRADE_DIRECTORY_SLUGS[trade] || "general-handyman";
          const href = `/fix-it/${serviceSlug}/${linkMetroSlug}`;
          const content = (
            <>
              <div className="flex items-center justify-center h-8 w-8">
                {TRADE_ICONS[trade] || <Wrench size={18} className="text-slate-600" />}
              </div>
              <span className="text-xs font-semibold text-slate-900">{TRADE_LABELS[trade] || trade}</span>
            </>
          );

          if (linkToDirectory) {
            return (
              <Link
                key={trade}
                href={href}
                className="flex flex-col items-center gap-2 p-3 rounded-lg border border-blue-100 bg-[#f7f9fd] hover:border-blue-300 hover:bg-blue-50 transition text-center"
                aria-label={`Browse ${TRADE_LABELS[trade] || trade} pros`}
              >
                {content}
              </Link>
            );
          }

          return (
            <div
              key={trade}
              className="flex flex-col items-center gap-2 p-3 rounded-lg border border-blue-100 bg-[#f7f9fd] hover:border-blue-300 hover:bg-blue-50 transition text-center"
            >
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );
}
